import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Phone, Mail, Check, Globe } from 'lucide-react';
import { useLang, ui } from '../i18n/LanguageContext';
import { team } from '../mock/data';
import { PersonPhoto } from '../components/PersonPhoto';
import useReveal from '../hooks/useReveal';

const TeamMember = () => {
  const { slug } = useParams();
  const { t } = useLang();
  useReveal();

  const member = team.find((m) => m.slug === slug);
  if (!member) {
    return (
      <div className="pt-40 pb-32 text-center">
        <p className="font-serif text-3xl mb-6">404</p>
        <Link to="/equipe" className="link-underline text-sm">{t(ui.cta.backToTeam)}</Link>
      </div>
    );
  }

  const expertise = t(member.expertise);

  return (
    <div>
      <section className="pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <Link to="/equipe" className="inline-flex items-center gap-2 text-muted-foreground hover:text-champagne text-sm mb-10 transition-colors">
            <ArrowLeft size={16} /> {t(ui.cta.backToTeam)}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-5 reveal">
              <div className="img-zoom aspect-[3/4] bg-secondary">
                <PersonPhoto name={member.name} photo={member.photo} className="w-full h-full" />
              </div>
            </div>

            <div className="lg:col-span-7 reveal">
              <p className="eyebrow text-champagne mb-4">{t(ui.team.tag)}</p>
              <h1 className="font-serif text-4xl md:text-5xl leading-tight">{member.name}</h1>
              <p className="text-lg text-champagne mt-3">{t(member.role)}</p>
              {member.subtitle && <p className="text-sm text-[#6b6862] mt-1">{t(member.subtitle)}</p>}

              {member.quote && (
                <blockquote className="mt-8 border-l-2 border-champagne pl-6">
                  <p className="font-serif italic text-xl md:text-2xl text-[#161514] leading-snug">{t(member.quote.text)}</p>
                  <cite className="not-italic block text-sm text-[#6b6862] mt-3">{t(member.quote.author)}</cite>
                </blockquote>
              )}

              {member.sections && <p className="eyebrow text-champagne mt-10 mb-4">{t(ui.team.about)}</p>}
              <div className={`space-y-5 text-[#3a3833] leading-relaxed text-lg ${member.sections ? '' : 'mt-8'}`}>
                {t(member.bio).map((p, i) => (
                  <p key={i} className={!member.quote && i === 1 && p.startsWith('«') ? 'font-serif italic text-xl text-[#161514]' : ''}>{p}</p>
                ))}
              </div>

              {member.sections && t(member.sections).map((sec, si) => (
                <div key={si} className="mt-10">
                  <h2 className="font-serif text-2xl md:text-3xl text-[#161514] mb-4">{sec.heading}</h2>
                  <div className="space-y-5 text-[#3a3833] leading-relaxed text-lg">
                    {(sec.paragraphs || []).map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                    {sec.list && (
                      <ul className="space-y-3">
                        {sec.list.map((item, i) => (
                          <li key={i} className="flex gap-3">
                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-champagne shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}

              {member.servicesOffered && (
                <div className="mt-10">
                  <p className="eyebrow text-champagne mb-4">{t(ui.team.servicesOffered)}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    {t(member.servicesOffered).map((s, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#3a3833]">
                        <Check size={17} className="text-champagne shrink-0 mt-1" strokeWidth={1.6} /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-10">
                <p className="eyebrow text-champagne mb-4">{t(ui.team.expertise)}</p>
                <ul className="flex flex-wrap gap-3">
                  {expertise.map((e, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm border border-border px-4 py-2">
                      <Check size={15} className="text-champagne" /> {e}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 bg-secondary p-7">
                <p className="eyebrow text-champagne mb-4">{member.outro ? t(ui.team.learnMore) : t(ui.team.contactMember)}</p>
                {member.outro && <p className="text-[#3a3833] leading-relaxed mb-6">{t(member.outro)}</p>}
                <div className="space-y-3 text-sm">
                  <a href={`mailto:${member.email}`} className="flex items-center gap-3 hover:text-champagne transition-colors">
                    <Mail size={17} className="text-champagne" /> {member.email}
                  </a>
                  {member.phone && (
                    <a href={`tel:${member.phone}`} className="flex items-center gap-3 hover:text-champagne transition-colors">
                      <Phone size={17} className="text-champagne" /> {member.phone}
                    </a>
                  )}
                  {member.website && (
                    <a href={member.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-champagne transition-colors" data-testid="member-website-link">
                      <Globe size={17} className="text-champagne" /> {t(ui.team.visitWebsite)}
                    </a>
                  )}
                </div>
                <Link to="/contact" className="inline-flex items-center gap-2 mt-6 bg-[#161514] text-white px-7 py-3 text-sm hover:bg-champagne transition-all duration-300">
                  {t(ui.cta.contact)} <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamMember;
